import {
    LayoutDashboard, FileText, FileEdit, Users, Truck, Receipt, ScanLine,
    FileCog, Banknote, Scale, BookOpen, Calculator, BarChart3,
    Settings, ShieldCheck, CreditCard, History, DatabaseZap, Compass, Anchor,
    Sparkles, Coins, CheckSquare,
} from "lucide-react";

export const HELP_CATEGORIES = [
    { id: "start", title: "Getting started" },
    { id: "new", title: "What's new" },
    { id: "sales", title: "Sales" },
    { id: "purchases", title: "Purchases" },
    { id: "banking", title: "Banking" },
    { id: "accounting", title: "Accounting" },
    { id: "workspace", title: "Workspace & admin" },
];

export const HELP_ARTICLES = [
    // ---------- Getting started ----------
    {
        slug: "scan-bill",
        category: "new",
        title: "Scan a bill with AI",
        icon: Sparkles,
        readTime: "2 min",
        summary: "Upload a supplier bill, let GPT-4o Vision fill in the draft, review, save.",
        body: [
            { h: "How it works" },
            { p: "Open Bills and click Scan bill in the header. Upload a PDF or image (up to 5 MB, multi-page PDFs are stitched up to 5 pages). We run it through GPT-4o Vision and extract the vendor name, invoice number, issue and due dates, currency, line items with quantities, unit prices, tax rates, totals, and any payment-terms notes." },
            { h: "Vendor matching — zero setup" },
            { p: "We fuzzy-match the extracted merchant against your existing vendors. If we find a good match we use it. If not, we auto-create a new vendor with the scanned name so the draft form saves in one click." },
            { h: "Attachment stays for audit" },
            { p: "The file is saved permanently to the created bill. A paperclip icon on the list row opens it in a new tab. Only members of your organisation can see it." },
            { h: "Quota" },
            { p: "AI scans share the monthly OCR pool with expense scans and receipt OCR. Free 5 / Pro 500 / Team 5,000 per month." },
        ],
    },
    {
        slug: "scan-expense",
        category: "new",
        title: "Scan a receipt into an expense",
        icon: ScanLine,
        readTime: "1 min",
        summary: "Same flow as Scan Bill, tuned for expense receipts — auto-category and all.",
        body: [
            { h: "How it works" },
            { p: "Open Expenses and click Scan receipt. Upload the receipt (PDF or image). We extract merchant, date, total, tax, currency, a suggested category, a short description, and a payment method guess." },
            { h: "What you review" },
            { p: "The Log expense dialog opens pre-filled. Vendor is matched against your list or auto-created. The receipt is already attached. Adjust anything you like and click Save." },
        ],
    },
    {
        slug: "multi-currency",
        category: "new",
        title: "Multi-currency, explained",
        icon: Coins,
        readTime: "2 min",
        summary: "Price anything in any currency. Live rates, user-override, locked on save.",
        body: [
            { h: "Why it matters" },
            { p: "Your base currency is set on the org (typically KYD for Cayman companies). Any invoice, bill, or expense can be in a different currency — BlueLedger will auto-fetch a live rate, let you override, and lock the rate on the document when you save. That frozen rate is what all your reports roll up on, so numbers never drift when rates move." },
            { h: "Where to find it" },
            { p: "Every create/edit form for Invoices, Bills, and Expenses has a currency picker plus an FX row. Pick a non-base currency and we call exchangerate.host (with Frankfurter and open.er-api.com as fallbacks). Click the refresh button to pull a fresh rate at any time." },
            { h: "On list rows" },
            { p: "Foreign-currency documents show a small chip next to the amount (e.g. 'USD · 1.20') so you can see at a glance which rate is applied. In-base-currency rows are clean." },
        ],
    },
    {
        slug: "bulk-actions",
        category: "new",
        title: "Bulk select and delete",
        icon: CheckSquare,
        readTime: "1 min",
        summary: "Multi-select invoices, bills, customers or vendors and act on all at once.",
        body: [
            { h: "How it works" },
            { p: "Tick the checkbox on one or more rows (or use the select-all header checkbox on Invoices and Bills). A blue bulk-action strip appears with a count and a Delete button. Click it, confirm, and we run the deletions in parallel." },
            { h: "Cascade safety" },
            { p: "Bulk delete honours the same rules as single-row delete. For bills and invoices, any recorded payments are also deleted so the bank ledger stays consistent — and the confirmation dialog warns you how many payments will go with them." },
        ],
    },
    {
        slug: "welcome",
        category: "start",
        title: "Welcome to BlueLedger",
        icon: Compass,
        readTime: "2 min",
        summary: "A quick tour of the app so you know where everything lives.",
        body: [
            { h: "What BlueLedger is for" },
            { p: "BlueLedger is a modern accounting platform for freelancers, small businesses, and finance teams — with first-class Cayman Islands jurisdiction support, including a no-tax mode that's on by default for KY companies." },
            { h: "The sidebar in one paragraph" },
            { p: "The left sidebar is grouped by workflow: Overview (Dashboard), Sales (Invoices, Estimates, Customers), Purchases (Bills, Expenses, Receipts, Vendors), Banking (Bank Accounts, Reconciliation), Accounting (Chart of Accounts, Journal Entries, Reports), and Workspace (Team & Roles, Import / Export, Subscription, Audit Log, Settings, Help Center)." },
            { h: "Your first 5 minutes" },
            { list: [
                "Open Settings → Tax & jurisdiction and confirm the Cayman no-tax toggle is set correctly.",
                "Upload your company logo in Settings → Branding.",
                "Add your first customer from Customers → Add customer.",
                "Create an invoice from Invoices → New invoice, send it, and record a payment.",
                "Head to Reports to see your first P&L update live.",
            ]},
        ],
    },
    {
        slug: "cayman-tax-mode",
        category: "start",
        title: "How Cayman no-tax mode works",
        icon: Anchor,
        readTime: "3 min",
        summary: "Why Cayman companies default to zero tax, and how to change it.",
        body: [
            { h: "The default" },
            { p: "When you set an organization's jurisdiction to Cayman Islands, BlueLedger enables no-tax mode automatically. Every new invoice and bill will compute tax as 0, regardless of what you type in the Tax % field on a line." },
            { h: "Why we do this" },
            { p: "Standard corporate income tax does not apply in the ordinary local Cayman context. Forcing 0 tax prevents accidental VAT/sales-tax errors on your reports." },
            { h: "When to turn it off" },
            { p: "Switch the toggle off in Settings → Tax & jurisdiction if you're invoicing customers in a VAT or sales-tax jurisdiction, handling tourism fees, or importing historical data that already contains tax amounts. You can also pick a Tax engine: No tax, VAT, Sales tax, or Custom." },
            { h: "Per-line overrides" },
            { p: "Even with no-tax mode on, you can type a Tax % on an invoice line — but it will be ignored until you disable no-tax mode at the organization level. This keeps your data clean." },
        ],
    },
    // ---------- Overview ----------
    {
        slug: "dashboard",
        category: "start",
        title: "Using the Dashboard",
        icon: LayoutDashboard,
        readTime: "2 min",
        summary: "Read the six KPIs, cash-flow chart, and expense mix at a glance.",
        body: [
            { h: "What each KPI means" },
            { list: [
                "Cash on hand — sum of current balances across all bank accounts.",
                "Revenue — invoiced revenue inside the selected time window (draft invoices are excluded).",
                "Expenses — all logged expenses plus bill totals inside the window (matches the cash-flow chart).",
                "Net — Revenue minus Expenses for the selected window.",
                "AR outstanding — unpaid/partial/overdue invoice balances.",
                "AP outstanding — unpaid bill balances.",
            ]},
            { h: "Time-range selector" },
            { p: "Use the range switcher at the top of the Dashboard (MTD · QTD · YTD · All time) to re-scope every KPI, the cash-flow chart, and the expense mix. YTD is the default." },
            { h: "Cash-flow chart" },
            { p: "The area chart shows income versus expenses for the last six months. Hover any point for the exact value. Amounts are reported in your organization's base currency." },
            { h: "Expense mix" },
            { p: "The donut shows your top expense categories. It breaks down by real Chart of Accounts expense categories — per-bill-line and per-expense — not a single 'Bills' bucket. Categories you create in the CoA automatically become slices." },
            { h: "Recent invoices & the Cayman tax card" },
            { p: "The right rail lists your five most recent invoices with status chips. The deep-ocean card confirms Cayman no-tax mode is active and links to Settings." },
        ],
    },
    // ---------- Sales ----------
    {
        slug: "invoices",
        category: "sales",
        title: "Invoices",
        icon: FileText,
        readTime: "4 min",
        summary: "Create, send, and collect payments on branded invoices.",
        body: [
            { h: "Creating an invoice" },
            { p: "Click New invoice. Pick a customer, set issue and due dates, add one or more line items (description, quantity, unit price). Under Cayman no-tax mode the Tax % field is disabled and totals compute with zero tax." },
            { h: "Statuses explained" },
            { list: [
                "Draft — saved but not sent. No accounting impact yet.",
                "Sent — issued to the customer; appears in AR.",
                "Partial — payments received but balance still owed.",
                "Paid — balance reached zero.",
                "Overdue — due date passed without full payment.",
                "Void — cancelled; excluded from reports.",
            ]},
            { h: "Sending & payments" },
            { p: "Draft invoices have a Send action (paper-plane icon). Once sent, use the $ icon to record a manual payment with amount and method. Partial payments update the balance and status automatically." },
            { h: "Tips" },
            { p: "Invoice numbers are generated automatically (INV-00001…). You can delete drafts; for sent invoices, void them instead so you keep the audit trail." },
        ],
    },
    {
        slug: "estimates",
        category: "sales",
        title: "Estimates (quotes)",
        icon: FileEdit,
        readTime: "2 min",
        summary: "Quote a customer, then convert to an invoice in one click.",
        body: [
            { h: "Purpose" },
            { p: "Estimates are non-binding proposals. They don't affect your books until you convert them into invoices. Use them to lock in scope and pricing with customers before work begins." },
            { h: "Converting to an invoice" },
            { p: "On any open estimate, click Convert. BlueLedger copies the customer, lines, totals, and notes into a new draft invoice. The original estimate is marked as Converted so it won't be counted twice." },
            { h: "Best practice" },
            { p: "Keep estimate numbers separate from invoice numbers. EST-00001 stays an estimate forever; the invoice it becomes gets its own INV-number." },
        ],
    },
    {
        slug: "customers",
        category: "sales",
        title: "Customers",
        icon: Users,
        readTime: "2 min",
        summary: "Manage the people and companies who owe you money.",
        body: [
            { h: "Adding customers" },
            { p: "Click Add customer. Name is required; email, phone, address, and tax ID are optional but helpful for invoice delivery and compliance." },
            { h: "From CSV" },
            { p: "For bulk loads, go to Import / Export → Customers → Download template, fill it in, and import. Zoho Books exports work directly." },
            { h: "Linking to invoices" },
            { p: "You can't delete a customer with open invoices — void or pay those first. Deleting a customer is soft-safe: historical invoice records keep the name as text." },
        ],
    },
    // ---------- Purchases ----------
    {
        slug: "bills",
        category: "purchases",
        title: "Bills",
        icon: FileCog,
        readTime: "3 min",
        summary: "Track what you owe vendors and pay it down over time.",
        body: [
            { h: "When to use a Bill" },
            { p: "Use Bills for anything you owe a vendor with a future due date — utilities, SaaS invoices, professional services, wholesale purchases. For one-off card/cash expenses, use Expenses instead." },
            { h: "Creating a bill" },
            { p: "Pick the vendor (or use the inline + button to add a new one on the fly), set issue and due dates, and add line items. Each line can have its own category — the dropdown is populated from your Chart of Accounts expense accounts, so any account you add in CoA (Utilities, Mortgage, Repairs and Maintenance, etc.) shows up here automatically." },
            { h: "Paying bills" },
            { p: "Click the $ icon on any open bill and enter the amount. Partial pay is supported; status flips to Partial then Paid. You can also select which Bank Account the money came out of and upload a receipt image/PDF for the audit trail." },
            { h: "Attach a receipt & auto-fill with AI" },
            { p: "On the Pay dialog, drop in a receipt image. If it's an image we show a 'Scan with AI' button that reads the receipt with GPT-4o vision and pre-fills the amount, paid-on date, and reference line. Images are compressed server-side so storage stays lean." },
            { h: "Aged payables report" },
            { p: "See what's coming due in Reports → Aged AP. Buckets: Current, 1–30, 31–60, 61–90, 90+ days overdue." },
        ],
    },
    {
        slug: "expenses",
        category: "purchases",
        title: "Expenses",
        icon: Receipt,
        readTime: "2 min",
        summary: "Log card and cash spending by category.",
        body: [
            { h: "Categories come from your Chart of Accounts" },
            { p: "The Category dropdown is populated live from every expense-type account in your Chart of Accounts. Add a new CoA account (Settings → Chart of Accounts → New account) and it appears in the dropdown instantly — no code change required. Categories drive the Dashboard expense mix, the P&L report, and the derived Trial Balance." },
            { h: "Vendor optional" },
            { p: "Linking an expense to a vendor is optional but recommended for purchases you make repeatedly — it gives you a per-vendor total inside Vendors. Use the inline + button in the vendor dropdown to create a new vendor without leaving the dialog." },
            { h: "Attach a receipt & auto-fill with AI" },
            { p: "Drop a receipt image or PDF directly onto the expense form (max 5 MB pre-compression). Images get compressed automatically. For images you'll also see a 'Scan with AI' button — one click reads the merchant, date, total, tax, and suggested category from the photo and fills the form." },
            { h: "Editing / duplicating" },
            { p: "Pencil-icon edits an expense; Copy-icon duplicates it for repeat costs like monthly software licences. The Receipt column on the list gives a one-click link to view the attached file." },
        ],
    },
    {
        slug: "receipts",
        category: "purchases",
        title: "Receipts & AI scanning",
        icon: ScanLine,
        readTime: "3 min",
        summary: "Snap a photo — we extract merchant, date, total, and category.",
        body: [
            { h: "Two ways to attach a receipt" },
            { list: [
                "Inside Bill pay or Expense dialogs — attach the receipt right where you're recording the spend. Best for day-to-day work.",
                "Standalone Receipts page — batch upload loose receipts ahead of time, then match them to transactions later.",
            ]},
            { h: "Supported files" },
            { p: "JPG, PNG, WEBP, HEIC, and PDF up to 5 MB pre-compression. Images are compressed server-side via Pillow (progressive JPEG, max 2000px) — a 4 MB phone photo often ends up around 200 KB without losing legibility." },
            { h: "AI Scan (images only)" },
            { p: "Click 'Scan with AI · auto-fill from receipt' after attaching an image. GPT-4o vision returns {merchant, date, total, tax, currency, suggested_category, line_items} and pre-fills the form. PDFs aren't scanned — copy the fields manually." },
            { h: "Monthly quota" },
            { p: "Free 5/mo, Pro 500/mo, Team 5,000/mo. Scans from the Bill pay / Expense dialogs share the same counter as standalone Receipts. Quota usage is surfaced in the response and on the Receipts page header." },
            { h: "Privacy" },
            { p: "Attachments are stored on the BlueLedger servers under your organization and are only visible to members of that organization. Cross-org access is blocked at the API layer." },
        ],
    },
    {
        slug: "vendors",
        category: "purchases",
        title: "Vendors",
        icon: Truck,
        readTime: "2 min",
        summary: "The suppliers you pay.",
        body: [
            { h: "What to capture" },
            { p: "Name, email, and phone are enough to start. Add address and tax ID for 1099 reporting or contract management." },
            { h: "From CSV" },
            { p: "The Import / Export page accepts Zoho Books Vendors exports directly. Extra columns are ignored." },
        ],
    },
    // ---------- Banking ----------
    {
        slug: "banking",
        category: "banking",
        title: "Bank accounts",
        icon: Banknote,
        readTime: "3 min",
        summary: "Add accounts, import statements, auto-categorize.",
        body: [
            { h: "Adding accounts" },
            { p: "Click Add account to register a checking, savings, or credit card. Enter the opening balance — BlueLedger starts the ledger from there." },
            { h: "Importing statements" },
            { p: "Select an account and click Import CSV. We detect columns like Date, Amount, and Description (case-insensitive). Duplicate rows (same date, amount, and description) are skipped automatically." },
            { h: "Auto-categorization" },
            { p: "On import we make a best guess for each transaction (Software, Meals, Travel, Utilities, Marketing, Salaries, Sales, Uncategorized). You can override anything in Reconciliation." },
            { h: "Auto-sync (Pro+)" },
            { p: "Real-time bank feed integrations are available on Pro and Team plans. Free plan supports manual CSV only — which is still more than enough for many businesses." },
        ],
    },
    {
        slug: "reconciliation",
        category: "banking",
        title: "Reconciliation",
        icon: Scale,
        readTime: "3 min",
        summary: "Reconcile the bank statement to your books.",
        body: [
            { h: "The two-column workflow" },
            { p: "The page splits into 'Needs review' and 'Reconciled'. Review happens top-down: confirm category, match to an invoice/bill/expense if relevant, click Reconcile. The transaction moves to the right." },
            { h: "Good habit" },
            { p: "Reconcile at the end of every month. Close the books by posting any end-of-month journal entries, then run Trial Balance to confirm debits = credits." },
        ],
    },
    // ---------- Accounting ----------
    {
        slug: "coa",
        category: "accounting",
        title: "Chart of Accounts",
        icon: BookOpen,
        readTime: "3 min",
        summary: "The backbone of your double-entry books.",
        body: [
            { h: "What it is" },
            { p: "Your Chart of Accounts (CoA) is the complete list of accounts you post to. Each account has a code, name, type (asset, liability, equity, revenue, expense), and an optional sub-type." },
            { h: "Defaults" },
            { p: "Every new organization is seeded with 19 sensible defaults — Cash, AR, Inventory, AP, Sales Revenue, Cost of Goods Sold, Salaries, Rent, Utilities, Marketing, and so on." },
            { h: "Add, edit, archive" },
            { p: "Click 'New account' to add one. On any row, the Pencil icon opens an edit dialog where you can rename, change the code, change the type/sub-type, or update the description. Duplicate codes within the same org are rejected. The Archive icon hides an account from new transactions but keeps it on historical reports — your audit trail is never lost." },
            { h: "Expense categories flow everywhere" },
            { p: "Expense-type accounts you create here automatically appear in the Category dropdowns on Bills and Expenses, and as slices in the Dashboard expense-mix pie chart. There's no separate 'categories' list to maintain." },
            { h: "Importing" },
            { p: "The Import / Export page accepts Zoho Books CoA exports. We normalize Account Type values like 'Income', 'Other Income', 'Operating Expense' to the internal types (asset, liability, equity, revenue, expense)." },
        ],
    },
    {
        slug: "journal-entries",
        category: "accounting",
        title: "Journal Entries",
        icon: Calculator,
        readTime: "3 min",
        summary: "Post manual debits and credits.",
        body: [
            { h: "When to use" },
            { p: "Use journal entries for adjustments, accruals, depreciation, prepayments, opening balances, or any transaction that doesn't fit into an invoice/bill/expense." },
            { h: "Rules" },
            { p: "A journal entry must balance: total debits must equal total credits to the penny. The Post button is disabled until it does. BlueLedger assigns a JE number automatically." },
            { h: "Audit trail" },
            { p: "Every posted entry is recorded in the Audit Log with the actor and timestamp. You cannot silently edit a posted entry — instead, post a reversing JE." },
        ],
    },
    {
        slug: "reports",
        category: "accounting",
        title: "Reports",
        icon: BarChart3,
        readTime: "4 min",
        summary: "P&L, Balance Sheet, Aged AR/AP, and Trial Balance.",
        body: [
            { h: "Live auto-refresh" },
            { p: "Every report re-queries on a short interval so the numbers you see reflect payments, bills, and expenses entered in other tabs without a manual refresh." },
            { h: "Profit & Loss" },
            { p: "Revenue minus cost of goods sold gives gross profit. Operating expenses are grouped by category (taken from bill-line categories + expense categories). The final line is net income. Void invoices are excluded automatically." },
            { h: "Balance Sheet" },
            { p: "As-of today. Assets include cash across all bank accounts and outstanding AR. Liabilities are outstanding AP. Equity is Assets minus Liabilities (retained earnings)." },
            { h: "Aged AR / Aged AP" },
            { p: "Buckets based on days overdue: Current, 1–30, 31–60, 61–90, 90+. Use these to prioritize collections and avoid late fees from vendors." },
            { h: "Trial Balance — live & derived" },
            { p: "BlueLedger produces a live, balanced Trial Balance from day one — no manual journal posting required. It combines (a) any manual journal entries you've posted with (b) synthetic double-entry activity from invoices (AR Dr / Revenue Cr), invoice payments (Cash Dr / AR Cr), bills (Expense Dr / AP Cr — split by bill-line category when present), bill payments (AP Dr / Cash Cr), and expenses (Expense Dr / Cash Cr). Totals always balance; accounts with no activity are hidden for readability." },
        ],
    },
    // ---------- Workspace ----------
    {
        slug: "team-roles",
        category: "workspace",
        title: "Team & Roles",
        icon: ShieldCheck,
        readTime: "3 min",
        summary: "Invite teammates and give them the right access.",
        body: [
            { h: "Roles at a glance" },
            { list: [
                "Owner — full access, one per org.",
                "Admin — everything except ownership transfer.",
                "Accountant — full accounting access, no billing.",
                "Manager — invoices, bills, customers, vendors.",
                "Staff — estimates, expenses, customer records.",
                "Auditor — read-only across the whole org.",
            ]},
            { h: "Inviting someone" },
            { p: "Click Invite, enter the teammate's email, pick a role, and send. If the email already has a BlueLedger account they're added instantly; otherwise we hold the seat until they sign up." },
            { h: "Seats" },
            { p: "Free = 1 user, Pro = 3, Team = 25. Over-limit downgrades don't kick anyone out — they just prevent adding more until you upgrade or remove someone." },
        ],
    },
    {
        slug: "import-export",
        category: "workspace",
        title: "Import / Export",
        icon: DatabaseZap,
        readTime: "3 min",
        summary: "Bring data in from your previous platform, export anytime.",
        body: [
            { h: "Supported entities" },
            { p: "Customers, Vendors, Chart of Accounts, Invoices, Bills, Sales Receipts, Customer Payments, Vendor Payments." },
            { h: "Templates" },
            { p: "Each card has a Template button. Download it to see the exact column names we accept. Extra columns are ignored, so Zoho Books exports work as-is." },
            { h: "Bills & invoices with line items" },
            { p: "One CSV row represents one line item. Rows that share the same Bill Number / Invoice Number are grouped into a single document." },
            { h: "Payments" },
            { p: "Customer Payments match by Invoice Number against existing invoices. Vendor Payments match by Bill Number. Unmatched rows are reported in the 'skipped' count; import the corresponding invoices/bills first." },
        ],
    },
    {
        slug: "subscription",
        category: "workspace",
        title: "Subscription & billing",
        icon: CreditCard,
        readTime: "3 min",
        summary: "Change plans, view invoices, and see billing events.",
        body: [
            { h: "Plans at a glance" },
            { list: [
                "Free — 1 business, 1 user, up to 50 invoices, 50 customers, 50 vendors, 5 OCR/mo.",
                "Pro — 3 businesses, 3 users, unlimited invoices / customers / vendors, 500 OCR/mo, bank feeds, recurring invoices.",
                "Team — 10 businesses, 25 users, unlimited invoices / customers / vendors, 5,000 OCR/mo, custom roles, white-label, 3-year audit retention.",
            ]},
            { h: "How limits are enforced" },
            { p: "Caps are checked the moment you try to create a new invoice, customer, vendor, or organisation. If you've hit the cap, we return a friendly 'you've hit your X cap on the Y plan — upgrade for unlimited' message so there are no silent failures. Estimates do NOT count toward the invoice cap, and void invoices are excluded so you can safely void a duplicate and create its replacement." },
            { h: "Organisation cap is user-level, not per-org" },
            { p: "The organisation cap applies across every org you belong to, not per org. The effective cap is the highest plan of any organisation you're a member of. Example: if you own a Pro org (cap 3) and you're also invited as Auditor on a Team org (cap 10), your effective cap is 10 — you can create up to 10 organisations." },
            { h: "Monthly or yearly" },
            { p: "Yearly billing saves 17%. Switch anytime — the new rate starts at the next renewal." },
            { h: "Downgrade behavior" },
            { p: "Downgrading to Free never deletes your data. Premium features stop working, but historical records remain. If you're already over a cap after the downgrade, existing records are kept — you just can't create new ones until you're back under the cap or upgrade again." },
            { h: "Billing events" },
            { p: "The side panel shows plan changes, renewals, failures, and cancellations with timestamps and amounts." },
        ],
    },
    {
        slug: "audit-log",
        category: "workspace",
        title: "Audit Log",
        icon: History,
        readTime: "2 min",
        summary: "Every important change, with who and when.",
        body: [
            { h: "What's logged" },
            { p: "Creating, updating, and deleting invoices, bills, expenses, receipts, customers, vendors, accounts, and journal entries. Also: role changes, plan changes, imports, and org setting updates." },
            { h: "Retention" },
            { p: "Free: 30 days. Pro: 180 days. Team: 3 years. Older entries roll off automatically." },
        ],
    },
    {
        slug: "settings",
        category: "workspace",
        title: "Settings",
        icon: Settings,
        readTime: "3 min",
        summary: "Organization info, jurisdiction, branding, and email defaults.",
        body: [
            { h: "Organization" },
            { p: "Business name, address, industry, base currency, and Contact email. The contact email appears as the Reply-To on every invoice, receipt, and customer statement email we send, so customer replies land in your inbox rather than ours. The business name is used as the From display-name on those emails — a polished 'Acme Ltd <billing@…>' lands in your customer's inbox." },
            { h: "Tax & jurisdiction" },
            { p: "Pick your country code. For Cayman Islands, we enable no-tax mode and pin Tax engine to 'No tax'. For other jurisdictions, choose VAT, Sales tax, or Custom and set a default rate." },
            { h: "Branding & logo on emails" },
            { p: "Upload a square PNG/JPG/WEBP logo (up to 1 MB) — it shows in the sidebar org switcher and is embedded at the top of every invoice, receipt, and customer-statement email your customers receive. Pick an accent color for customer-facing pages." },
            { h: "Invoice defaults (templates & terms)" },
            { p: "Choose one of three invoice-email templates — Classic (clean blue cards), Modern (bold dark header with high-contrast total), or Minimal (monochrome serif, print-ready). Each card shows a real mini-preview of the email; click 'Preview full size' to see the exact email your customer will receive. Set default payment terms once and they auto-fill on every new invoice." },
        ],
    },
];

export function articlesByCategory(category) {
    return HELP_ARTICLES.filter((a) => a.category === category);
}

export function findArticle(slug) {
    return HELP_ARTICLES.find((a) => a.slug === slug);
}
